import React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import type { ActionProps, DisplayOptionProps, ImageSizesProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { MediaBlockClassNames, MediaBlockMedia, MediaOriginalSizeProps } from './mediaBlock.state'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { htmlDecode, nl2br, unEntity } from '../../../utils/html'

export const mediaBlockClasses: MediaBlockClassNames = {
  // DEFAULTS
  block: 'block block--media',
  inview: 'inview',
  inner: 'block__inner ls-gutter ls-spacing--s',
  header: 'block__header ls-container--nogut',
  label: 'block__label text--s',
  title: 'block__title title--m',
  text: 'block__intro text--l',
  intro: 'block__intro text--l',
  image: 'block__image',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--tertiary',

  // BLOCK SPECIFIC
  figure: 'media',
  figureImage: 'media--image',
  figureVideo: 'media--video',
  figureContent: 'media--cw ls-container--nogut',
  figureFull: 'media--fw ls-container--l-nogut',
  figcaption: 'media__meta',
  caption: 'media__caption',
  source: 'media__source text--s',
}

export const MediaBlockLayout = {
  Inner: (props: { children?: React.ReactNode; amountNested: number }): JSX.Element => {
    const { amountNested, children } = props
    return (
      <div className={getClassNameModern(mediaBlockClasses, 'inner', 'mw-container-m mx-auto px-gutter')}>
        {children}
      </div>
    )
  },
  Content: (props: { label?: string; title?: string; intro?: string }): JSX.Element => {
    const { label, title, intro } = props
    return label || title || intro ? (
      <header className={getClassNameModern(mediaBlockClasses, 'header', 'mw-container-s mx-auto px-gutter')}>
        {label && (
          <p className={getClassNameModern(mediaBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>{label}</p>
        )}
        {title && <h2 id={slugify(htmlDecode(title))} className={getClassNameModern(mediaBlockClasses, 'title', 'h2 mb-3')}>{title}</h2>}
        {intro && (
          <div
            className={getClassNameModern(mediaBlockClasses, 'text', 'text--styled')}
            dangerouslySetInnerHTML={{ __html: nl2br(unEntity(intro)) }}
          />
        )}
      </header>
    ) : (
      <></>
    )
  },
  Visual: (props: { item: MediaBlockMedia; id: string; children: JSX.Element }) => {
    const { item, id, children } = props
    const { displayOption, type, caption, source } = item
    return (
      <><figure
        key={`media-${id}`}
        className={`${getClassNameModern(mediaBlockClasses, 'figure', 'mt-3')}
        ${getClassNameModern(
          mediaBlockClasses,
          displayOption === 'full-width' ? 'figureFull' : 'figureContent',
          displayOption === 'full-width' ? '' : 'mw-container-s mx-auto px-gutter'
        )}
        ${getClassNameModern(mediaBlockClasses, type === 'image' ? 'figureImage' : 'figureVideo', '')}`}
      >
        {children}
        {item.type == 'video' && <p className='print-only'>Video URL: {item.url}</p>}

        {(caption || source) && (
          <figcaption
            className={getClassNameModern(mediaBlockClasses, 'figcaption', 'mt-3 text-center figure-caption')}
          >
            {caption && (
              <span
                className={getClassNameModern(mediaBlockClasses, 'caption', 'd-block')}
                dangerouslySetInnerHTML={{ __html: caption }} />
            )}
            {source && (
              <span
                className={getClassNameModern(mediaBlockClasses, 'source', '')}
                dangerouslySetInnerHTML={{ __html: source }} />
            )}
          </figcaption>
        )}
      </figure>
      </>
    )
  },
  Image: (props: {originalSize: MediaOriginalSizeProps, displayOption: DisplayOptionProps } & ImageSizesProps) => {
    const { displayOption, originalSize, image } = props
    return (
      <SharedBlockLayout.ModernImageSrcSet
        image={image}
        lazyload={true}
        priority={undefined}
        classNames={`${getClassNameModern(mediaBlockClasses, 'image', 'd-block w-100')} ${originalSize.showinOriginalSize == true ? 'original-size' : 'default-size'}`}
        pictureClassNames={`block__picture ${originalSize.showinOriginalSize == true ? 'original-size' : ''}`}
        imageProportions={{
          width: originalSize.showinOriginalSize == true ? `${originalSize.width}` : displayOption == 'content-width' ? '866' : '1312',
          height: originalSize.showinOriginalSize == true ? `${originalSize.height}` : displayOption == 'content-width' ? '487' : '738', // 16:9 to reserve some space
          sizes: originalSize.showinOriginalSize == true ? `${originalSize.width}w` : displayOption == 'content-width' ? '(min-width: 1024px) 866px, 100vw' : '(min-width: 1440px) 1312px,100vw'
        }}
      />
    )
  },
  Footer: (props: ActionProps) => {
    const {
      showPrimary,
      showSecondary,
      primaryAction,
      secondaryAction,
    } = props
    return (
      <>
        {(showPrimary || showSecondary) && (
          <footer className='block__actions ls-container--nogut'>
            <div className={getClassNameModern(mediaBlockClasses, 'footer', 'pt-5')}>
              {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
                <div className={getClassNameModern(mediaBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}>
                  <SharedBlockLayout.Clickable
                    id='textPrimaryAction'
                    type='button'
                    link={primaryAction}
                    classNames={getClassNameModern(mediaBlockClasses, 'clickablePrimary', 'btn btn-primary')}
                  />
                </div>
              )}
              {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
                <div
                  className={getClassNameModern(
                    mediaBlockClasses,
                    'clickableWrapper',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <SharedBlockLayout.Clickable
                    id='textSecondaryAction'
                    type='button'
                    link={secondaryAction}
                    classNames={getClassNameModern(mediaBlockClasses, 'clickableSecondary', 'btn btn-outline-primary')}
                  />
                </div>
              )}
            </div>
          </footer>
        )}
      </>
    )
  },
}
